import i18n from 'js/localization/i18n';
import _ from 'lodash';
import {
  NumberFormatter,
  RangeFormatter,
  ArrayFormatter,
} from 'innovatint_base/common/formatters/NumberFormatter';
import {
  EI_TYPE_CURRENCY,
  EI_TYPE_LARGE_VOLUME,
  EI_TYPE_LARGE_MASS,
  EI_TYPE_PERCENTAGE,
  EI_TYPE_SMALL_VOLUME,
  EI_TYPE_SMALL_MASS,
  EI_TYPE_AREA,
  EI_TYPE_STRING,
  EI_TYPE_NUMBER,
  EI_TYPE_ARRAY,
  EI_VARIABLES,
} from 'innovatint_base/common/EIVariables';
import { RGBColorToHexOrNull } from 'js/mylib/Utils';
import { EXTRA_INFO_APPLY_DISCOUNT, EXTRA_INFO_DISCOUNT } from 'js/Constants';
import { clone } from 'chart.js/helpers';
import { EXTRA_INFO_PRICEGROUP } from '../../Constants';
import type { OrderitemCanPrice } from 'js/api/types/Pricing';
import type { CacheProduct } from '../types/Cache';
import type {
  ConfigurationState,
  JsonValue,
  SelectedCurrency,
} from '../types/Configuration';
import type { OrderState } from '../types/Order';

type ExtraInfoValue =
  | JsonValue
  | number[]
  | Array<number | null>
  | null
  | undefined;
type ExtraInfoPriceData = OrderitemCanPrice &
  Record<string, ExtraInfoValue> & {
    baseAdjusted?: number;
    cntAdjusted?: number;
    hardenerAdjusted?: number;
  };
type ExtraInfoFormatter = {
  format(value: any): string;
};
type CurrencyFormatters = {
  default: ExtraInfoFormatter;
  selected: ExtraInfoFormatter;
};
type ExtraInfoFormatterMap = Record<
  string,
  ExtraInfoFormatter | CurrencyFormatters | any
>;
type ExtraInfoDatasetRow = {
  code: string;
  dataType: string;
  value: ExtraInfoValue;
  formatted: string;
  selected_currency_formatted: string;
};
type ExtraInfoValueMap = Record<string, string>;
type UnitDefinition = {
  symbol: string;
  scale: number;
};
type UfiComponent = {
  volume: number;
  uficode?: string | null;
};
type NullableNumber = number | null | undefined;

/**
 * All extra info values as strings
 *
 * @param {Object} order Redux state.order
 * @param {Object} configurations Redux state.configurations
 * @param {Boolean} excIncTax If true, add _incTax and _excTax values.
 * @param [Boolean] forcePriceoptionLot Calculate price of all cans regardless
 *    of priceoption_lot setting
 * @returns {Object} {code: string value}
 */
export default function EIValues(
  order: OrderState,
  configurations: ConfigurationState,
  excIncTax = false,
  forcePriceoptionLot = false
): ExtraInfoValueMap {
  return _.fromPairs(
    EIDataset(order, configurations, excIncTax, forcePriceoptionLot).map(
      (x) => [x.code, x.selected_currency_formatted]
    )
  );
}

export function EIDataset(
  order: OrderState,
  configurations: ConfigurationState,
  excIncTax = false,
  forcePriceoptionLot = false
): ExtraInfoDatasetRow[] {
  const formatters = makeFormatters(configurations);

  if (!formatters) return {} as ExtraInfoDatasetRow[];

  const data = order.item.price;
  if (!data) return {} as ExtraInfoDatasetRow[];

  const { rgb, lab, xyz } = order.color || {};
  const product = order.product as Partial<CacheProduct> | null;
  const additionalValues = {
    rgb,
    rgbHex: RGBColorToHexOrNull(rgb),
    tsr: order.item.tsr,
    lab,
    xyz,
    y: xyz ? xyz[1] : null,
    productDescription: product?.description,
    hardenerName: product?.hardener?.productzname,
    canCode: order.can?.cancode,
    bfsGrade: bfsGrade(order),
    formulaUFIs: formulaUFIs(order),
  };

  const ncansMultiplier =
    forcePriceoptionLot || configurations.config_values.priceoption_lot
      ? order.item.ncans
      : 1;

  /**
   * Apply colorant / base adjustments
   */

  const applyAdjustments = (input: OrderitemCanPrice): ExtraInfoPriceData => {
    const prices = clone(input) as ExtraInfoPriceData;
    const base_factor = 1 + order.item.basepriceadjustment; // 30% discount for base read from oder.item.base_adjustment
    const cnt_factor = 1 + order.item.cntpriceadjustment; // 30% discount for base read from oder.item.base_adjustment

    if (base_factor !== 1) {
      let new_price = (prices.basePrice || 0) * base_factor;
      prices.baseAdjusted = new_price - (prices.basePrice || 0);
      prices.basePrice = (prices.basePrice || 0) + prices.baseAdjusted;
      prices.price = (prices.price || 0) + prices.baseAdjusted;

      new_price = (prices.hardenerPrice || 0) * base_factor;
      prices.hardenerAdjusted = new_price - (prices.hardenerPrice || 0);
      prices.hardenerPrice =
        (prices.hardenerPrice || 0) + prices.hardenerAdjusted;
      prices.price = (prices.price || 0) + prices.hardenerAdjusted;
    }
    if (cnt_factor !== 1) {
      const new_price = (prices.cntPrice || 0) * cnt_factor;
      prices.cntAdjusted = new_price - (prices.cntPrice || 0);
      prices.cntPrice = (prices.cntPrice || 0) + prices.cntAdjusted;
      prices.price = (prices.price || 0) + prices.cntAdjusted;
    }

    return prices;
  };

  const adjustedData = {
    ...data,
    excTaxCan: applyAdjustments(data.excTaxCan),
    incTaxCan: applyAdjustments(data.incTaxCan),
  };

  const defaultTax = configurations.config_values.priceoption_exctax
    ? adjustedData.excTaxCan
    : adjustedData.incTaxCan;

  const { priceoption_pricegroup_naming } = configurations.config_values;
  const price_group_rename = new Map<ExtraInfoValue, ExtraInfoValue>();

  if (Array.isArray(priceoption_pricegroup_naming)) {
    priceoption_pricegroup_naming.forEach((v, i) =>
      price_group_rename.set(i, v as ExtraInfoValue)
    );
  }

  const defaultData = { ...defaultTax, ...additionalValues };
  const r: ExtraInfoDatasetRow[] = [];
  /**
   * Apply discount to adjustedData here
   */

  const dis_fac = 1 - order.item.discount;

  for (let { code, dataType, isTaxable, isScalable } of EI_VARIABLES()) {
    const discounted = (val: ExtraInfoValue): ExtraInfoValue =>
      EXTRA_INFO_APPLY_DISCOUNT.includes(code)
        ? dis_fac * Number(val || 0)
        : val;

    const scaled = (val: ExtraInfoValue): ExtraInfoValue => {
      if (isScalable) {
        if (dataType === EI_TYPE_AREA) {
          if (Array.isArray(val)) {
            return val.map((x) =>
              typeof x === 'number' ? x * ncansMultiplier : x
            ); // preserve nulls
          }
        } else {
          return Number(val || 0) * ncansMultiplier;
        }
      }
      return val;
    };

    const getValue = (obj: ExtraInfoPriceData): ExtraInfoValue =>
      scaled(
        code === EXTRA_INFO_DISCOUNT
          ? Number(obj.price || 0) * order.item.discount
          : discounted(obj[code])
      );

    let formatter = formatters[dataType] as ExtraInfoFormatter;
    let selected_frmt = formatters[dataType] as ExtraInfoFormatter;
    // Currency has default and selected formatters!
    if (dataType === EI_TYPE_CURRENCY) {
      formatter = (formatters[dataType] as CurrencyFormatters).default;
      selected_frmt = (formatters[dataType] as CurrencyFormatters).selected;
    }

    const value =
      code === EXTRA_INFO_PRICEGROUP
        ? price_group_rename.has(defaultData[code])
          ? price_group_rename.get(defaultData[code])
          : defaultData[code]
        : getValue(defaultData);
    r.push({
      code,
      dataType,
      value,
      formatted: formatter.format(value),
      selected_currency_formatted: selected_frmt.format(value),
    });

    if (excIncTax && isTaxable) {
      const exc = getValue(adjustedData.excTaxCan);
      r.push({
        code: code + '_excTax',
        dataType,
        value: exc,
        formatted: formatter.format(exc),
        selected_currency_formatted: selected_frmt.format(exc),
      });

      const inc = getValue(adjustedData.incTaxCan);
      r.push({
        code: code + '_incTax',
        dataType,
        value: inc,
        formatted: formatter.format(inc),
        selected_currency_formatted: selected_frmt.format(inc),
      });
    }
  }
  // Calculate discount values

  return r;
}

class StringFormatter {
  format(value: unknown): string {
    return value != null ? String(value) : '';
  }
}

export function makeFormatters(
  configurations: ConfigurationState
): ExtraInfoFormatterMap | null {
  if (!configurations.zone) return null;

  const massUnits: Record<string, UnitDefinition> = {
    kg: { symbol: i18n.t('symbol.kg', 'kg'), scale: 0.001 },
    lb: { symbol: i18n.t('symbol.lb', 'lb'), scale: 1 / 453.59237 },
    g: { symbol: i18n.t('symbol.g', 'g'), scale: 1 },
  };

  const volumeUnits: Record<string, UnitDefinition> = {
    l: { symbol: i18n.t('symbol.l', 'l'), scale: 0.001 }, // ml -> L
    ml: { symbol: i18n.t('symbol.ml', 'ml'), scale: 1 }, // ml -> ml
    'U.S. gal': {
      symbol: i18n.t('symbol.us_gal', 'U.S. gal'),
      scale: 0.26417205236 * 0.001,
    },
    'U.S. quarts': {
      symbol: i18n.t('symbol.us_quarts', 'U.S. quarts'),
      scale: 1.0566882094 * 0.001,
    },
    'Imp. gal': {
      symbol: i18n.t('symbol.imp_gal', 'Imp. gal'),
      scale: 0.2199692483 * 0.001,
    },
    'U.S. fl. oz.': {
      symbol: i18n.t('symbol.us_fl_oz', 'U.S. fl. oz.'),
      scale: 33.8140227 * 0.001,
    },
    'Imp. fl. oz.': {
      symbol: i18n.t('symbol.imp_fl_oz', 'Imp. fl. oz.'),
      scale: 35.1950652 * 0.001,
    },
  };

  const massUnitKey =
    (configurations.config_values.priceoption_mass_unit as string) || 'g';
  const { symbol: massSymbol, scale: massScale } =
    massUnits[massUnitKey] || massUnits.g;

  const volumeUnitKey =
    (configurations.config_values.priceoption_volume_unit as string) || 'ml';
  const { symbol: volumeSymbol, scale: volumeScale } =
    volumeUnits[volumeUnitKey] || volumeUnits.ml;

  const mass_formatter = new NumberFormatter({
    decimals: configurations.config_values
      .priceoption_mass_decimals as NullableNumber,
    symbol: massSymbol,
    scale: massScale,
  });

  const volume_formatter = new NumberFormatter({
    decimals: configurations.config_values
      .priceoption_volume_decimals as NullableNumber,
    symbol: volumeSymbol,
    scale: volumeScale,
  });

  const fallback = i18n.t('symbol.na', 'n/a');
  const selected_currency = _.get(
    configurations.temporary,
    'selected_currency'
  ) as SelectedCurrency;

  return {
    [EI_TYPE_CURRENCY]: {
      default: new NumberFormatter({
        decimals: configurations.zone.currencydecimals,
        allowTrailingZeros:
          !!configurations.config_values.priceoption_trailing_zeros,
        symbol: configurations.zone.currencysymbol || '',
        currencyFormat: configurations.zone.currencyformat ?? undefined,
        fallback,
      }),
      selected: new NumberFormatter({
        decimals: selected_currency.decimals,
        allowTrailingZeros:
          !!configurations.config_values.priceoption_trailing_zeros,
        symbol: selected_currency.symbol,
        scale: selected_currency.rate,
        currencyFormat: configurations.zone.currencyformat ?? undefined,
        fallback,
      }),
    },
    [EI_TYPE_SMALL_VOLUME]: volume_formatter,
    [EI_TYPE_LARGE_VOLUME]: volume_formatter,
    [EI_TYPE_SMALL_MASS]: mass_formatter,
    [EI_TYPE_LARGE_MASS]: mass_formatter,
    [EI_TYPE_PERCENTAGE]: new NumberFormatter({
      decimals: configurations.config_values
        .priceoption_percentage_decimals as NullableNumber,
      symbol: '%',
      scale: 100,
      fallback,
    }),
    [EI_TYPE_AREA]: new RangeFormatter({ symbol: 'm²', decimals: 1 }),
    [EI_TYPE_ARRAY]: new ArrayFormatter({}),
    [EI_TYPE_NUMBER]: new NumberFormatter(),
    [EI_TYPE_STRING]: new StringFormatter(),
  };
}

function bfsGrade(order: OrderState): string | null {
  const {
    base,
    item: { cnts },
  } = order;
  const baseGrade = base ? base.bfsgrade : '';
  const cntGrades = cnts ? cnts.map((x) => x.bfsgrade) : [];

  // require all components to have a grade
  if (baseGrade == null || cntGrades.includes(null)) {
    return null;
  }

  cntGrades.sort((a, b) => Number(b) - Number(a));
  const cntGrade = cntGrades.length ? cntGrades[0] : '';
  return baseGrade + cntGrade;
}

const UFI_CONCENTRATION_THRESHOLD = 0.05;

function UFIstring(totalvolume: number, { volume, uficode }: UfiComponent) {
  const conc = volume / totalvolume;
  const concString =
    conc > UFI_CONCENTRATION_THRESHOLD
      ? ' ' + (100 * conc).toFixed(0) + '\u00A0%'
      : '';
  return 'UFI:' + uficode + concString;
}

function formulaUFIs(order: OrderState): string | null {
  const {
    base,
    can,
    item: { cnts },
  } = order;
  if (!base || base.disableufi || !can || !cnts) {
    return null;
  }
  const components: UfiComponent[] = [
    ...cnts,
    { volume: can.basevolume || 0, uficode: base.uficode },
  ].sort((a, b) => b.volume - a.volume); // sort by descending volume
  // TODO: use cumulativevolume above

  const totalvolume = components.reduce((a, b) => a + b.volume, 0);
  const UFIstrings = components
    .filter((x) => x.uficode)
    .map((x) => UFIstring(totalvolume, x));
  return UFIstrings.join('\n');
}
